// ============================================================================
// CorpseLayer - Renders corpses on the ground
// ============================================================================

import { EntityType, RotStage, TILE_SIZE } from '@dreamgame/shared';
import { Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import { allCorpses, type CorpseData } from '../../state/corpses';
import { loadTexture, onCategoryReload } from '../TextureManager';

// ============================================================================
// Module State
// ============================================================================

let container: Container;
let graphics: Graphics;

const corpseTexts: Map<number, Text> = new Map();
const corpseSprites: Map<number, Sprite> = new Map();
const _usedTextIds: Set<number> = new Set();
const _usedSpriteIds: Set<number> = new Set();

// Textures
let pawnCorpseTexture: Texture | null = null;
let npcCorpseTexture: Texture | null = null; // rabbit for all animals

// ============================================================================
// Colors for corpse states
// ============================================================================

const CORPSE_COLORS = {
  PAWN_FRESH: 0x8b6914, // Brown/tan for fresh pawn corpse
  PAWN_STALE: 0x6b5210, // Darker brown for stale
  PAWN_ROTTED: 0x4a3a0a, // Very dark brown/grey for rotted
  PAWN_SKELETON: 0xc0c0c0, // Light grey for skeleton

  ANIMAL_FRESH: 0x8b4513, // Saddle brown for fresh animal
  ANIMAL_STALE: 0x6b3510, // Darker brown for stale
  ANIMAL_ROTTED: 0x4a2a0a, // Very dark for rotted
  ANIMAL_SKELETON: 0xd2b48c, // Tan/bone color for skeleton
} as const;

const ROT_TINTS: Record<number, number> = {
  [RotStage.FRESH]: 0xffffff,
  [RotStage.STALE]: 0xcccc88,
  [RotStage.ROTTED]: 0x888866,
  [RotStage.SKELETON]: 0xaaaaaa,
};

// ============================================================================
// Layer Creation
// ============================================================================

export const createCorpseLayer = (): { container: Container; graphics: Graphics } => {
  container = new Container();
  graphics = new Graphics();
  return { container, graphics };
};

// ============================================================================
// Texture Loading
// ============================================================================

export const loadCorpseTextures = async (): Promise<void> => {
  try {
    pawnCorpseTexture = await loadTexture({ path: '/corpses/pawn_corpse.png' });
    console.log('Loaded pawn corpse texture');
  } catch {
    console.warn('Failed to load pawn corpse texture, using fallback');
  }

  try {
    npcCorpseTexture = await loadTexture({ path: '/corpses/rabbit_corpse.png' });
    console.log('Loaded animal corpse texture (rabbit)');
  } catch {
    console.warn('Failed to load animal corpse texture, using fallback');
  }
};

// Reload textures (clears sprites and reloads from TextureManager)
export const reloadCorpseTextures = async (): Promise<void> => {
  // Clear all existing sprites
  clearCorpseVisuals();

  // Clear textures
  pawnCorpseTexture = null;
  npcCorpseTexture = null;

  // Reload textures
  await loadCorpseTextures();
  console.log('Corpse textures reloaded');
};

// Register for texture reload events
onCategoryReload({ category: 'corpses', callback: reloadCorpseTextures });

// ============================================================================
// Rendering
// ============================================================================

// Render a single corpse (sprite or fallback)
const renderCorpse = ({
  corpse,
  worldX,
  worldY,
}: {
  corpse: CorpseData;
  worldX: number;
  worldY: number;
}): void => {
  const texture =
    corpse.originalEntityType === EntityType.PAWN ? pawnCorpseTexture : npcCorpseTexture;

  if (texture) {
    drawCorpseSprite({ worldX, worldY, corpse, texture });
  } else if (corpse.originalEntityType === EntityType.PAWN) {
    drawPawnCorpseFallback({ worldX, worldY, corpse });
  } else {
    drawNpcCorpseFallback({ worldX, worldY, corpse });
  }
};

// Hide unused sprites and texts
const cleanupUnusedCorpseVisuals = (): void => {
  for (const [id, sprite] of corpseSprites) {
    if (!_usedSpriteIds.has(id)) {
      sprite.visible = false;
    }
  }

  for (const [id, text] of corpseTexts) {
    if (!_usedTextIds.has(id)) {
      text.visible = false;
    }
  }
};

export const drawCorpses = (): void => {
  graphics.clear();
  _usedTextIds.clear();
  _usedSpriteIds.clear();

  const corpseList = allCorpses.value;

  for (const corpse of corpseList) {
    const worldX = corpse.x * TILE_SIZE;
    const worldY = corpse.y * TILE_SIZE;

    renderCorpse({ corpse, worldX, worldY });

    if (corpse.originalEntityType === EntityType.PAWN && corpse.name) {
      drawCorpseName({ worldX, worldY, corpse });
    }
  }

  cleanupUnusedCorpseVisuals();
};

const drawCorpseSprite = ({
  worldX,
  worldY,
  corpse,
  texture,
}: {
  worldX: number;
  worldY: number;
  corpse: CorpseData;
  texture: Texture;
}): void => {
  _usedSpriteIds.add(corpse.networkId);

  const tint = ROT_TINTS[corpse.rotStage] ?? 0xffffff;

  const existingSprite = corpseSprites.get(corpse.networkId);
  if (existingSprite) {
    existingSprite.x = worldX;
    existingSprite.y = worldY;
    existingSprite.tint = tint;
    existingSprite.visible = true;
    if (existingSprite.texture !== texture) {
      existingSprite.texture = texture;
    }
  } else {
    const sprite = new Sprite(texture);
    sprite.width = TILE_SIZE;
    sprite.height = TILE_SIZE;
    sprite.x = worldX;
    sprite.y = worldY;
    sprite.tint = tint;
    container.addChild(sprite);
    corpseSprites.set(corpse.networkId, sprite);
  }
};

const drawPawnCorpseFallback = ({
  worldX,
  worldY,
  corpse,
}: {
  worldX: number;
  worldY: number;
  corpse: CorpseData;
}): void => {
  const corpseWidth = TILE_SIZE * 0.8;
  const corpseHeight = TILE_SIZE * 0.4;
  const offsetX = (TILE_SIZE - corpseWidth) / 2;
  const offsetY = TILE_SIZE - corpseHeight - 4;

  // Get color based on rot stage
  const baseColor = getPawnCorpseColor({ rotStage: corpse.rotStage });
  const tint = ROT_TINTS[corpse.rotStage] ?? 0xffffff;

  // Shadow
  graphics.setFillStyle({ color: 0x000000, alpha: 0.3 });
  graphics.ellipse(worldX + TILE_SIZE / 2, worldY + TILE_SIZE - 2, corpseWidth / 2, 4);
  graphics.fill();

  // Body (horizontal oval representing lying down)
  graphics.setFillStyle({ color: applyTint({ baseColor, tint }) });
  graphics.ellipse(
    worldX + offsetX + corpseWidth / 2,
    worldY + offsetY + corpseHeight / 2,
    corpseWidth / 2,
    corpseHeight / 2
  );
  graphics.fill();

  // Head (small circle)
  graphics.setFillStyle({ color: applyTint({ baseColor: 0xffe4c4, tint }) });
  graphics.circle(worldX + offsetX + 4, worldY + offsetY + corpseHeight / 2, 4);
  graphics.fill();

  // Skeleton indicator (X marks on body for skeleton stage)
  if (corpse.rotStage === RotStage.SKELETON) {
    graphics.setStrokeStyle({ width: 1, color: 0x666666 });
    // Ribs
    for (let i = 0; i < 3; i++) {
      const ribX = worldX + offsetX + 12 + i * 6;
      graphics.moveTo(ribX, worldY + offsetY + 2);
      graphics.lineTo(ribX, worldY + offsetY + corpseHeight - 2);
    }
    graphics.stroke();
  }

  // Border
  graphics.setStrokeStyle({ width: 1, color: 0x000000, alpha: 0.5 });
  graphics.ellipse(
    worldX + offsetX + corpseWidth / 2,
    worldY + offsetY + corpseHeight / 2,
    corpseWidth / 2,
    corpseHeight / 2
  );
  graphics.stroke();
};

const drawNpcCorpseFallback = ({
  worldX,
  worldY,
  corpse,
}: {
  worldX: number;
  worldY: number;
  corpse: CorpseData;
}): void => {
  const corpseWidth = TILE_SIZE * 0.6;
  const corpseHeight = TILE_SIZE * 0.35;
  const offsetX = (TILE_SIZE - corpseWidth) / 2;
  const offsetY = TILE_SIZE - corpseHeight - 4;

  // Get color based on rot stage
  const baseColor = getNpcCorpseColor({ rotStage: corpse.rotStage });
  const tint = ROT_TINTS[corpse.rotStage] ?? 0xffffff;

  // Shadow
  graphics.setFillStyle({ color: 0x000000, alpha: 0.25 });
  graphics.ellipse(worldX + TILE_SIZE / 2, worldY + TILE_SIZE - 2, corpseWidth / 2, 3);
  graphics.fill();

  // Body (rounded rect for animal carcass)
  graphics.setFillStyle({ color: applyTint({ baseColor, tint }) });
  graphics.roundRect(worldX + offsetX, worldY + offsetY, corpseWidth, corpseHeight, 4);
  graphics.fill();

  // Show meat/leather yield indicator for fresh corpses
  if (corpse.rotStage === RotStage.FRESH || corpse.rotStage === RotStage.STALE) {
    if (corpse.meatYield > 0) {
      // Small meat indicator
      graphics.setFillStyle({ color: 0xcc4444 });
      graphics.roundRect(worldX + offsetX + corpseWidth - 6, worldY + offsetY + 2, 4, 4, 1);
      graphics.fill();
    }
  }

  // Skeleton stage - bone pattern
  if (corpse.rotStage === RotStage.SKELETON) {
    graphics.setStrokeStyle({ width: 1, color: 0x666666 });
    // Simple bone pattern
    graphics.moveTo(worldX + offsetX + 4, worldY + offsetY + corpseHeight / 2);
    graphics.lineTo(worldX + offsetX + corpseWidth - 4, worldY + offsetY + corpseHeight / 2);
    graphics.stroke();
  }

  // Border
  graphics.setStrokeStyle({ width: 1, color: 0x000000, alpha: 0.4 });
  graphics.roundRect(worldX + offsetX, worldY + offsetY, corpseWidth, corpseHeight, 4);
  graphics.stroke();
};

const drawCorpseName = ({
  worldX,
  worldY,
  corpse,
}: {
  worldX: number;
  worldY: number;
  corpse: CorpseData;
}): void => {
  _usedTextIds.add(corpse.networkId);

  const textX = worldX + TILE_SIZE / 2;
  const textY = worldY - 10;
  const displayName = `${corpse.name}`;

  const existingText = corpseTexts.get(corpse.networkId);
  if (existingText) {
    existingText.text = displayName;
    existingText.x = textX;
    existingText.y = textY;
    existingText.visible = true;
  } else {
    const text = new Text({
      text: displayName,
      style: {
        fontFamily: 'Arial',
        fontSize: 9,
        fill: 0xcccccc,
        stroke: { color: 0x000000, width: 2 },
      },
    });
    text.anchor.set(0.5, 1);
    text.x = textX;
    text.y = textY;
    container.addChild(text);
    corpseTexts.set(corpse.networkId, text);
  }
};

// ============================================================================
// Helpers
// ============================================================================

const getPawnCorpseColor = ({ rotStage }: { rotStage: number }): number => {
  switch (rotStage) {
    case RotStage.FRESH:
      return CORPSE_COLORS.PAWN_FRESH;
    case RotStage.STALE:
      return CORPSE_COLORS.PAWN_STALE;
    case RotStage.ROTTED:
      return CORPSE_COLORS.PAWN_ROTTED;
    case RotStage.SKELETON:
      return CORPSE_COLORS.PAWN_SKELETON;
    default:
      return CORPSE_COLORS.PAWN_FRESH;
  }
};

const getNpcCorpseColor = ({ rotStage }: { rotStage: number }): number => {
  switch (rotStage) {
    case RotStage.FRESH:
      return CORPSE_COLORS.ANIMAL_FRESH;
    case RotStage.STALE:
      return CORPSE_COLORS.ANIMAL_STALE;
    case RotStage.ROTTED:
      return CORPSE_COLORS.ANIMAL_ROTTED;
    case RotStage.SKELETON:
      return CORPSE_COLORS.ANIMAL_SKELETON;
    default:
      return CORPSE_COLORS.ANIMAL_FRESH;
  }
};

const applyTint = ({ baseColor, tint }: { baseColor: number; tint: number }): number => {
  // Simple multiplicative tint
  const br = (baseColor >> 16) & 0xff;
  const bg = (baseColor >> 8) & 0xff;
  const bb = baseColor & 0xff;

  const tr = (tint >> 16) & 0xff;
  const tg = (tint >> 8) & 0xff;
  const tb = tint & 0xff;

  const r = Math.floor((br * tr) / 255);
  const g = Math.floor((bg * tg) / 255);
  const b = Math.floor((bb * tb) / 255);

  return (r << 16) | (g << 8) | b;
};

// ============================================================================
// Cleanup
// ============================================================================

export const removeCorpseVisuals = ({ networkId }: { networkId: number }): void => {
  const text = corpseTexts.get(networkId);
  if (text) {
    text.destroy();
    corpseTexts.delete(networkId);
  }

  const sprite = corpseSprites.get(networkId);
  if (sprite) {
    sprite.destroy();
    corpseSprites.delete(networkId);
  }
};

export const clearCorpseVisuals = (): void => {
  for (const text of corpseTexts.values()) {
    text.destroy();
  }
  corpseTexts.clear();

  for (const sprite of corpseSprites.values()) {
    sprite.destroy();
  }
  corpseSprites.clear();
};
